import type { Project, SyncOperation, SyncStatus, Task } from "./types";
import { supabase } from "./supabase";
import { nowIso, uid } from "./utils";
import { queueOperation, readQueue, reconcileRemoteProjects, removeQueueItem, replaceRemoteRows } from "./db";

const MAX_RETRY_DELAY_MS = 60_000;

export function makeOperation(
  entity: SyncOperation["entity"],
  action: SyncOperation["action"],
  recordId: string,
  payload: SyncOperation["payload"],
): SyncOperation {
  return {
    id: uid(),
    entity,
    action,
    record_id: recordId,
    payload,
    tries: 0,
    created_at: nowIso(),
  };
}

export async function enqueue(entity: SyncOperation["entity"], action: SyncOperation["action"], recordId: string, payload: SyncOperation["payload"]) {
  await queueOperation(makeOperation(entity, action, recordId, payload));
}

export async function pullRemote(userId: string): Promise<{ tasks: Task[]; projects: Project[]; removedProjectIds: string[] }> {
  if (!supabase) return { tasks: [], projects: [], removedProjectIds: [] };
  const [tasksResult, projectsResult] = await Promise.all([
    supabase.from("tasks").select("*").eq("user_id", userId),
    supabase.from("projects").select("*").eq("user_id", userId),
  ]);
  if (tasksResult.error) throw tasksResult.error;
  if (projectsResult.error) throw projectsResult.error;
  const tasks = (tasksResult.data ?? []) as Task[];
  const projects = (projectsResult.data ?? []) as Project[];
  await Promise.all([
    replaceRemoteRows("tasks", userId, tasks),
    replaceRemoteRows("projects", userId, projects),
  ]);
  const removedProjectIds = await reconcileRemoteProjects(userId, projects);
  return { tasks, projects, removedProjectIds };
}

export async function flushQueue(onStatus: (patch: Partial<SyncStatus>) => void, options: { force?: boolean } = {}): Promise<void> {
  if (!supabase || !navigator.onLine) return;
  const operations = await readQueue();
  if (!operations.length) {
    onStatus({ pending: 0, error: null });
    return;
  }

  const now = Date.now();
  const ready: SyncOperation[] = [];
  for (const operation of operations) {
    if (options.force || !operation.next_attempt_at || Date.parse(operation.next_attempt_at) <= now) {
      ready.push(operation);
    } else {
      break;
    }
  }

  if (!ready.length) {
    onStatus({
      pending: operations.length,
      error: operations.find((operation) => operation.last_error)?.last_error ?? null,
      nextRetryAt: operations.find((operation) => operation.next_attempt_at)?.next_attempt_at ?? null,
    });
    return;
  }

  onStatus({ syncing: true, pending: operations.length, error: null });
  try {
    for (const operation of ready) {
      const { error } = await writeOperation(operation);
      if (error) {
        await markFailed(operation, error.message);
        throw error;
      }
      await removeQueueItem(operation.id);
    }
    onStatus({ pending: 0, error: null, nextRetryAt: null });
  } catch (error) {
    onStatus({ error: error instanceof Error ? error.message : "Sync failed" });
  } finally {
    const pending = await readQueue();
    onStatus({
      syncing: false,
      pending: pending.length,
      nextRetryAt: pending.find((operation) => operation.next_attempt_at)?.next_attempt_at ?? null,
    });
  }
}

async function markFailed(operation: SyncOperation, message: string): Promise<void> {
  const tries = operation.tries + 1;
  const delay = Math.min(MAX_RETRY_DELAY_MS, 1000 * 2 ** Math.min(tries, 6));
  await queueOperation({
    ...operation,
    tries,
    last_error: message,
    next_attempt_at: new Date(Date.now() + delay).toISOString(),
  });
}

function writeOperation(operation: SyncOperation) {
  if (!supabase) throw new Error("Supabase is not configured");
  if (operation.entity === "tasks") {
    const table = supabase.from("tasks") as unknown as RemoteTable;
    return operation.action === "delete"
      ? table.update(operation.payload).eq("id", operation.record_id)
      : table.upsert(operation.payload as Task);
  }
  const table = supabase.from("projects") as unknown as RemoteTable;
  return operation.action === "delete"
    ? table.delete().eq("id", operation.record_id)
    : table.upsert(operation.payload as Project);
}

interface RemoteTable {
  delete(): { eq(column: string, value: string): PromiseLike<{ error: Error | null }> };
  update(payload: unknown): { eq(column: string, value: string): PromiseLike<{ error: Error | null }> };
  upsert(payload: unknown): PromiseLike<{ error: Error | null }>;
}
